import React, { useEffect, useRef, useState } from "react";
import { graphql } from "gatsby";
import { Link } from "./link";
import sortPosition from "../utility/sort-position";
import classNames from "classnames";
import VectorRight from "../icons/vector-right.svg";
import slugify from "@sindresorhus/slugify";
import EditorialIntro from "./editorial-intro";

const AssistanceFooter = ({ data }) => {
  const title = data.assistanceFooterModule.moduleTitle;
  const abstract = data.assistanceFooterModule.abstract;
  const titleSectionField = data.assistanceFooterModule.titleSectionFieldSet;
  const sections = titleSectionField.filter((item) => item.titleSection).sort(sortPosition);
  const isMegaContentServiceVersion =
    data?.assistanceFooterModule?.enableMegaContentServiceVersion?.[0] === "yes" ? true : false;

  const textRefs = useRef(new Array(sections.length).fill(0));
  const [elmsStyle, setElmsStyle] = useState();
  useEffect(() => {
    if (textRefs.current.length) {
      const maxData = textRefs.current.reduce(
        (res, el) => {
          const { top, height } = el.getBoundingClientRect();
          if (res.top === 0 || res.top === top) {
            return res.height > height ? res : { top, height, num: res.num + 1 };
          }
          return res;
        },
        { top: 0, num: 0 }
      );
      if (maxData.height && maxData.num > 1) {
        setElmsStyle({ minHeight: maxData.height });
      }
    }
  }, []);

  const [region, setRegion] = useState();

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch(`/api-geo/`);
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setRegion(data["country-region"]);
      } catch (err) {
        console.error("Error calling api-geo: ", err);
      }
    };
    loadCountries();
  }, []);

  return (
    <>
      {data?.assistanceFooterModule?.anchorDesktop && (
        <span
          id={slugify(data.assistanceFooterModule.anchorDesktop)}
          className="tabs__scroll-anchor"
        />
      )}
      <section className="section section--dark pb-2">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2
                className={classNames(
                  "box-contact-title",
                  isMegaContentServiceVersion && "box-contact--light-title"
                )}
              >
                {title}
              </h2>
              {abstract && (
                <div
                  className={classNames(
                    "box-contact-desc",
                    isMegaContentServiceVersion && "box-contact--light-desc"
                  )}
                  dangerouslySetInnerHTML={{ __html: abstract }}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div
                className={`d-flex flex-wrap justify-content-center box-contact-container num-box-${sections.length}`}
              >
                {sections.map((item, i) => (
                  <BoxContact
                    key={i}
                    item={item}
                    i={i}
                    elmsStyle={elmsStyle}
                    textRefs={textRefs}
                    region={region}
                    isMegaContentServiceVersion={isMegaContentServiceVersion}
                  />
                ))}
              </div>
            </div>
          </div>
          {data?.assistanceFooterModule?.assistanceFooterEditorialIntro &&
            data?.assistanceFooterModule?.assistanceFooterEditorialIntro?.liferayFields
              ?.typeName === "HAIER - ST - ECOMM - EDITORIAL" && (
              <div className="row mt-3">
                <div className="col-12">
                  <EditorialIntro
                    data={data.assistanceFooterModule.assistanceFooterEditorialIntro}
                    origin="assistance-footer"
                  />
                </div>
              </div>
            )}
        </div>
      </section>
    </>
  );
};

const BoxContact = ({ item, i, elmsStyle, textRefs, region, isMegaContentServiceVersion }) => {
  const isRussiaBox = item.iconSectionFieldSet?.some((icon) => icon.russiaCode);

  const isRussiaRegion = item.iconSectionFieldSet?.some((icon) =>
    icon.russiaCode?.split(",")?.includes(region)
  );

  let icons;
  let isAnchor;

  if (isRussiaBox) {
    if (isRussiaRegion) {
      icons = item.iconSectionFieldSet.filter(
        (icon) => !icon.russiaCode || icon.russiaCode?.split(",")?.includes(region)
      );
    } else if (region && region !== "not available") {
      icons = item.iconSectionFieldSet.filter((icon) => !icon.russiaCode);
    } else {
      icons = item.iconSectionFieldSet.filter(
        (icon) => !icon.russiaCode || icon.russiaCode?.split(",")?.includes("VOR")
      );
    }

    isAnchor = true;
  } else {
    icons = item.iconSectionFieldSet;
  }

  const icon = icons?.slice(0, 1);

  return (
    <div
      key={i}
      className={classNames("box-contact", isMegaContentServiceVersion && " box-contact--light")}
      id={isAnchor ? "callAnchor" : "box-contact" + i}
    >
      <div
        ref={(el) => (textRefs.current[i] = el)}
        className={classNames("box-contact__text", isMegaContentServiceVersion && "col-9")}
        style={elmsStyle}
      >
        <h4
          className={classNames(
            !isMegaContentServiceVersion && "box-contact__title",
            isMegaContentServiceVersion && "box-contact--light__title"
          )}
        >
          {item.titleSection}
        </h4>
        {item?.description && (
          <div
            className={classNames(isMegaContentServiceVersion && "box-contact--light__description")}
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}

        <Link
          to={icon?.[0]?.link}
          className={classNames(
            "d-none",
            isMegaContentServiceVersion && "box-contact--light__link d-flex align-items-center"
          )}
        >
          {icon?.[0]?.label}
          {!icon?.[0].link.includes("tel:") && !icon?.[0].link.includes("mailto:") && (
            <VectorRight
              className={classNames(
                "d-none",
                isMegaContentServiceVersion && "box-contact--light__link-arrow"
              )}
            />
          )}
        </Link>
      </div>
      <div
        className={classNames(
          "box-contact__contact",
          isMegaContentServiceVersion &&
            "box-contact--light__contact col-3 d-flex justify-content-center"
        )}
      >
        {!isMegaContentServiceVersion &&
          icons &&
          icons
            .filter(
              (icon) =>
                icon.iconSection &&
                icon.link &&
                icon.label &&
                icon.iconSection.node &&
                icon.iconSection.node.publicURL
            )
            .map((icon, j) => (
              <div key={j} className="mb-2">
                <Link to={icon.link} className="d-flex align-items-center">
                  <img
                    src={icon.iconSection.node.publicURL}
                    className="box-contact__icon"
                    alt={icon?.iconSection?.value?.description || ""}
                  />
                  <span>{icon.label}</span>
                </Link>
              </div>
            ))}
        {isMegaContentServiceVersion && icon && (
          <div className="mb-2">
            <img
              src={icon?.[0]?.iconSection?.node?.publicURL}
              className="box-contact--light__icon"
              alt={icon?.[0]?.iconSection?.value?.description || ""}
            />
          </div>
        )}
        {!isMegaContentServiceVersion && item && item.note && (
          <div
            className="box-contact__disclaimer"
            dangerouslySetInnerHTML={{ __html: item.note }}
          />
        )}
      </div>
    </div>
  );
};

export default AssistanceFooter;
export const fragment = graphql`
  fragment AssistanceFooterFragment on LiferayAssistanceFooter {
    id
    liferayFields {
      siteId
      articleId
    }
    assistanceFooterModule: moduleTitleFieldSet {
      moduleTitle
      anchorDesktop
      anchorMobile
      enableMegaContentServiceVersion
      abstract
      titleSectionFieldSet {
        titleSection
        position
        description
        iconSectionFieldSet {
          iconSection {
            node {
              publicURL
            }
            value {
              description
            }
          }
          label
          link
          russiaCode
        }
        note
      }
      assistanceFooterEditorialIntro {
        ...EditorialFragment
      }
    }
  }
`;
